import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useAuthState } from '../useAuthState/useAuthState'
import { calculateRetryDelay, shouldRetryQuery } from '../useInfiniteQuery/useInfiniteQuery'

import { CurrencyCode, formatMoney } from '@percent/utility'
import { PartnerScopes } from '@percent/partner-dashboard/constants/scopes'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { resolveRewardMoney } from './resolveRewardMoney'

export type PointsRewardValue = Readonly<{
  isLoading: boolean
  isRewardConfigured: boolean
  formatReward: (points: number | null | undefined, rewardsEligible: boolean) => string | undefined
}>

export const usePointsRewardValue = (): PointsRewardValue => {
  const { partner } = useAuthState()
  const { gamificationService } = useServices()
  const {
    i18n: { language }
  } = useTranslation()

  const hasGamificationScope = !!partner?.scopes?.some(
    ({ scopeName, deletedAt }) => !deletedAt && scopeName === PartnerScopes.Gamification
  )

  const { data, isPending } = useQuery({
    queryKey: ['getGamificationPartnerConfig'],
    queryFn: async () =>
      (await gamificationService.getGamificationConfig()).data.data?.points_rewards_configuration ?? null,
    enabled: hasGamificationScope,
    staleTime: 1000 * 60,
    retry: (failureCount, error) => shouldRetryQuery(failureCount, error),
    retryDelay: attemptIndex => calculateRetryDelay(attemptIndex)
  })

  const currency = partner?.currencyCode as CurrencyCode | undefined

  const formatReward = useCallback(
    (points: number | null | undefined, rewardsEligible: boolean) => {
      const reward = rewardsEligible ? resolveRewardMoney(data ?? undefined, currency, points) : undefined

      return reward ? formatMoney(reward, { locale: language, hideDecimalsIfZero: true }) : undefined
    },
    [data, currency, language]
  )

  return {
    isLoading: hasGamificationScope && isPending,
    isRewardConfigured: !!data,
    formatReward
  }
}
